import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Tag, Loader2, Edit2, Save, X, Plus, ExternalLink, AlertCircle, Copy } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * Admin → Pro Member Deals
 *
 * CRUD over /api/admin/pro-perks. Manages real, verified partner discount
 * codes surfaced on the Pro page to subscribed members. Codes are stored in
 * the `pro_perks` MongoDB collection and NEVER seeded — the section stays
 * hidden until at least one enabled perk exists.
 */
export default function ProDealsCard() {
  const [perks, setPerks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // perk_id | "_new"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pro-perks");
      setPerks(data.perks || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    setEditing(p.perk_id);
    setForm({ ...p });
  };

  const startNew = () => {
    setEditing("_new");
    setForm({ brand: "", offer: "", code: "", url: "", enabled: true, expires_at: "", sort: 0 });
  };

  const cancel = () => { setEditing(null); setForm({}); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        brand: (form.brand || "").trim(),
        offer: (form.offer || "").trim(),
        code: (form.code || "").trim(),
        url: (form.url || "").trim(),
        enabled: form.enabled !== false,
        expires_at: (form.expires_at || "").trim() || null,
        sort: Number(form.sort) || 0,
      };
      if (editing === "_new") {
        await api.post("/admin/pro-perks", payload);
        toast.success(`"${payload.brand}" added`);
      } else {
        await api.patch(`/admin/pro-perks/${editing}`, payload);
        toast.success("Saved");
      }
      cancel();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (perk_id, brand) => {
    if (!window.confirm(`Delete "${brand}" perk? Pro members will no longer see this code.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/pro-perks/${perk_id}`);
      toast.success(`"${brand}" deleted`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const enabledCount = perks.filter((p) => p.enabled).length;

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="pro-deals-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> Pro member deals
          </div>
          <div className="font-display text-xl mt-1">Partner discount codes</div>
          <p className="text-sm text-[#555] max-w-2xl mt-1">
            Manage real, verified discount codes shown to Pro subscribers on the <code className="mono text-[12px]">/pro</code> page.
            The section is hidden entirely until at least one perk is enabled —
            so the page never advertises an empty promise.
          </p>
        </div>
        <button onClick={startNew} className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5" data-testid="new-perk-btn">
          <Plus className="w-3.5 h-3.5" /> New perk
        </button>
      </div>

      {/* Status banner */}
      <div className={`border hairline p-3 mb-4 text-[12px] flex items-start gap-2 ${enabledCount > 0 ? "bg-[#f4fff0]" : "bg-[#fff4f0]"}`} data-testid="perks-status">
        {enabledCount > 0 ? (
          <>
            <span className="text-[#0a8500]">✓</span>
            <div><strong>{enabledCount} live perk{enabledCount === 1 ? "" : "s"}.</strong> Pro members see them in the Member Deals section on the Pro page.</div>
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4 text-[#b00020] flex-shrink-0 mt-0.5" />
            <div>
              <strong>No live perks.</strong> The Member Deals section is hidden on <code className="mono">/pro</code>. Add a verified code to start showing it.
            </div>
          </>
        )}
      </div>

      {/* New-perk inline form */}
      {editing === "_new" && (
        <PerkEditRow form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew />
      )}

      {/* List */}
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : perks.length === 0 ? (
        <div className="p-8 border hairline bg-[#fafafa] text-center text-sm text-[#888]">No perks yet — tap "New perk" to add one.</div>
      ) : (
        <div className="border hairline divide-y" data-testid="perk-list">
          {perks.map((p) => (
            editing === p.perk_id ? (
              <div key={p.perk_id} className="p-4 bg-[#fafafa]">
                <PerkEditRow form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} />
              </div>
            ) : (
              <div key={p.perk_id} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 hover:bg-[#fafafa]" data-testid={`perk-${p.perk_id}`}>
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base break-words">{p.brand}</span>
                    <code className="mono text-[11px] bg-[#ffefea] text-[#ff4500] px-1.5 py-0.5">{p.code}</code>
                    {!p.enabled && <span className="mono text-[10px] uppercase text-[#888]">disabled</span>}
                    {p.expires_at && <span className="mono text-[10px] uppercase text-[#888]">exp {p.expires_at}</span>}
                  </div>
                  <div className="text-[13px] text-[#333] mt-1 break-words">{p.offer}</div>
                  <div className="text-[12px] text-[#555] mt-1 min-w-0">
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#ff4500] underline break-all inline-flex items-baseline gap-1">
                      <span className="break-all">{p.url}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap sm:flex-nowrap sm:justify-end w-full sm:w-auto pt-1 sm:pt-0 border-t sm:border-t-0 hairline sm:border-0">
                  <button
                    onClick={() => { navigator.clipboard.writeText(p.code); toast.success("Code copied"); }}
                    className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1"
                    data-testid={`copy-${p.perk_id}`}>
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <button onClick={() => startEdit(p)} className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1" data-testid={`edit-${p.perk_id}`}>
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(p.perk_id, p.brand)} className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1 text-[#b00020]" data-testid={`del-${p.perk_id}`}>
                    <X className="w-3 h-3" /> Del
                  </button>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function PerkEditRow({ form, setForm, onSave, onCancel, busy, isNew = false }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-2.5 mb-4">
      <div className="grid sm:grid-cols-2 gap-2.5">
        <div>
          <div className="eyebrow text-[10px] mb-1">Brand</div>
          <input value={form.brand || ""} onChange={(e) => set("brand", e.target.value)}
            className="w-full border hairline p-2 text-sm" placeholder="Mountain Warehouse" data-testid="perk-brand" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">Discount code</div>
          <input value={form.code || ""} onChange={(e) => set("code", e.target.value.toUpperCase())}
            className="w-full border hairline p-2 text-sm mono" placeholder="CAMP15" data-testid="perk-code" />
        </div>
      </div>
      <div>
        <div className="eyebrow text-[10px] mb-1">Offer description</div>
        <input value={form.offer || ""} onChange={(e) => set("offer", e.target.value)}
          className="w-full border hairline p-2 text-sm" placeholder="15% off everything, full price only" data-testid="perk-offer" />
      </div>
      <div className="grid sm:grid-cols-[1fr_180px_100px] gap-2.5">
        <div>
          <div className="eyebrow text-[10px] mb-1">Link URL</div>
          <input value={form.url || ""} onChange={(e) => set("url", e.target.value)}
            className="w-full border hairline p-2 text-sm mono" placeholder="https://www.mountainwarehouse.com" data-testid="perk-url" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">Expires (optional)</div>
          <input value={form.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)}
            className="w-full border hairline p-2 text-sm mono" placeholder="2026-12-31" data-testid="perk-expires" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">Sort order</div>
          <input type="number" value={form.sort ?? 0} onChange={(e) => set("sort", e.target.value)}
            className="w-full border hairline p-2 text-sm mono" placeholder="0" data-testid="perk-sort" />
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} className="accent-[#ff4500]" data-testid="perk-enabled" />
          Enabled (visible to Pro members)
        </label>
        <div className="flex items-center gap-1.5">
          <button onClick={onCancel} disabled={busy} className="btn-secondary !px-3 !py-2 text-xs" data-testid="perk-cancel">Cancel</button>
          <button onClick={onSave} disabled={busy || !form.brand || !form.offer || !form.code || !form.url}
            className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
            data-testid="save-perk-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {isNew ? "Add perk" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
